/**
 * Página pública mobile-first: roteiro do técnico via token.
 * sem dados sensíveis hardcoded — tudo via API pública.
 */
import React, { useEffect, useState } from 'react';
import { fetchPublicTechnicianRoadmap } from '../services/technicianPanelApi';
import './TechnicianPublicRoadmapPage.css';

function formatForecastLabel(ymd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return '';
  const [y, m, d] = ymd.split('-').map((x) => parseInt(x, 10));
  try {
    return new Date(y, m - 1, d).toLocaleDateString('pt-BR', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
    });
  } catch {
    return ymd;
  }
}

function formatPrevisao(v) {
  if (!v) return '';
  try {
    const date = new Date(v);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function formatWhen(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

export default function TechnicianPublicRoadmapPage() {
  const [state, setState] = useState({ loading: true, data: null, error: '' });

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams(window.location.search);
    const token = String(params.get('t') || params.get('token') || '').trim();
    if (!token) {
      setState({ loading: false, data: null, error: 'Link inválido.' });
      return undefined;
    }
    (async () => {
      try {
        const data = await fetchPublicTechnicianRoadmap(token);
        if (cancelled) return;
        setState({ loading: false, data, error: '' });
      } catch (err) {
        if (cancelled) return;
        setState({
          loading: false,
          data: null,
          error: err?.message || 'Não foi possível carregar o roteiro.',
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const data = state.data;
  const status = data?.status;

  return (
    <div className="tpr-page">
      <div className="tpr-shell">
        <header className="tpr-header">
          <div className="tpr-brand">Roteiro técnico</div>
          {status === 'ok' && (
            <div className="tpr-day-pill" data-day={data.planDay}>
              {data.planDayLabel || (data.planDay === 'hoje' ? 'Hoje' : 'Amanhã')}
            </div>
          )}
        </header>

        {state.loading && (
          <div className="tpr-status tpr-status--load">
            <span className="tpr-spinner" aria-hidden="true" />
            Carregando roteiro…
          </div>
        )}

        {!state.loading && state.error && !data && (
          <div className="tpr-status tpr-status--err">{state.error}</div>
        )}

        {!state.loading && status === 'not_yet_valid' && (
          <div className="tpr-status tpr-status--wait">
            <h1>Quase lá</h1>
            <p>{data.message || 'Este roteiro ainda não está disponível.'}</p>
            {data.validFrom && (
              <p className="tpr-meta">Disponível a partir de {formatWhen(data.validFrom)} (Brasília)</p>
            )}
          </div>
        )}

        {!state.loading && (status === 'expired' || status === 'revoked' || status === 'invalid') && (
          <div className="tpr-status tpr-status--err">
            <h1>Indisponível</h1>
            <p>{data?.message || state.error || 'Link inválido ou expirado.'}</p>
          </div>
        )}

        {!state.loading && status === 'ok' && (
          <>
            <section className="tpr-hero">
              <h1 className="tpr-tech">{data.technicianName}</h1>
              <p className="tpr-sub">
                Roteiro de <strong>{data.planDayLabel || (data.planDay === 'hoje' ? 'hoje' : 'amanhã')}</strong>
                {data.forecast ? ` · ${formatForecastLabel(data.forecast)}` : ''}
              </p>
              <p className="tpr-count">
                {data.orders?.length || 0} ordem{(data.orders?.length || 0) === 1 ? '' : 's'} de serviço
              </p>
            </section>

            <ol className="tpr-list">
              {(data.orders || []).map((o) => (
                <li key={`${o.sequence}-${o.os}`} className="tpr-card">
                  <div className="tpr-card-top">
                    <span className="tpr-n">{o.sequence}</span>
                    <div className="tpr-card-main">
                      <div className="tpr-client">{o.cliente || '—'}</div>
                      <div className="tpr-os">#{o.os}</div>
                    </div>
                    {o.calcPrevisao ? (
                      <span className="tpr-sla">{formatPrevisao(o.calcPrevisao)}</span>
                    ) : null}
                  </div>
                  {(o.equipamento || o.serie || o.pat) && (
                    <div className="tpr-row">
                      {o.equipamento ? <span>{o.equipamento}</span> : null}
                      {o.serie ? <span>Série {o.serie}</span> : null}
                      {o.pat ? <span>Pat. {o.pat}</span> : null}
                    </div>
                  )}
                  {(o.endereco || o.bairro || o.cidade) && (
                    <div className="tpr-addr">
                      {[o.endereco, o.bairro, o.cidade].filter(Boolean).join(' · ')}
                    </div>
                  )}
                  {(o.solicitante || o.defeito || o.defeitoObs || o.observacaoGeral) && (
                    <div className="tpr-details">
                      {o.solicitante ? (
                        <div className="tpr-detail">
                          <span className="tpr-detail-k">Solicitante</span>
                          <span className="tpr-detail-v">{o.solicitante}</span>
                        </div>
                      ) : null}
                      {o.defeito ? (
                        <div className="tpr-detail">
                          <span className="tpr-detail-k">Defeito</span>
                          <span className="tpr-detail-v">{o.defeito}</span>
                        </div>
                      ) : null}
                      {o.defeitoObs ? (
                        <div className="tpr-detail">
                          <span className="tpr-detail-k">Observação do defeito</span>
                          <span className="tpr-detail-v">{o.defeitoObs}</span>
                        </div>
                      ) : null}
                      {o.observacaoGeral ? (
                        <div className="tpr-detail">
                          <span className="tpr-detail-k">Observação geral</span>
                          <span className="tpr-detail-v">{o.observacaoGeral}</span>
                        </div>
                      ) : null}
                    </div>
                  )}
                </li>
              ))}
            </ol>

            {data.expiresAt && (
              <footer className="tpr-footer">
                Link válido até {formatWhen(data.expiresAt)} (Brasília)
              </footer>
            )}
          </>
        )}
      </div>
    </div>
  );
}
